import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CampoTipoAtivo, CampoTipoAtivoFormData } from '@/types/tipo-ativo';
import { useClientFilter } from '@/hooks/useClientFilter';

export const useCamposTipoAtivo = (tipoAtivoId?: string) => {
  const { getClientFilter } = useClientFilter();
  const clientId = getClientFilter();

  return useQuery({
    queryKey: ['campos-tipo-ativo', tipoAtivoId, clientId],
    queryFn: async () => {
      if (!tipoAtivoId) return [];
      
      const { data, error } = await supabase
        .from('cmdb_campos_tipo_ativo')
        .select('*')
        .eq('tipo_ativo_id', tipoAtivoId)
        .eq('ativo', true)
        .order('ordem');

      if (error) throw error;
      return data as CampoTipoAtivo[];
    },
    enabled: !!tipoAtivoId,
  });
};

export const useCreateCampoTipoAtivo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CampoTipoAtivoFormData) => {
      const { data: result, error } = await supabase
        .from('cmdb_campos_tipo_ativo')
        .insert({
          ...data,
          configuracoes: data.configuracoes as any,
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campos-tipo-ativo', variables.tipo_ativo_id] });
      toast.success('Campo customizado criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar campo: ' + error.message);
    },
  });
};

export const useUpdateCampoTipoAtivo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, tipoAtivoId, data }: { id: string; tipoAtivoId: string; data: Partial<CampoTipoAtivoFormData> }) => {
      const { data: result, error } = await supabase
        .from('cmdb_campos_tipo_ativo')
        .update({
          ...data,
          configuracoes: data.configuracoes as any,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campos-tipo-ativo', variables.tipoAtivoId] });
      toast.success('Campo customizado atualizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar campo: ' + error.message);
    },
  });
};

export const useDeleteCampoTipoAtivo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, tipoAtivoId }: { id: string; tipoAtivoId: string }) => {
      const { error } = await supabase
        .from('cmdb_campos_tipo_ativo')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campos-tipo-ativo', variables.tipoAtivoId] });
      toast.success('Campo customizado deletado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao deletar campo: ' + error.message);
    },
  });
};
